import { useSim } from "@/context/SimContext";
import { Card, StatusRow, SectionTitle, StatusDot } from "@/components/ui-kit";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Cpu, Activity, ShieldCheck, Gauge, Zap, Clock, ArrowRight } from "lucide-react";

function fmtDuration(s) {
  if (s == null) return "00:00:00";
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");
  return `${h}:${m}:${sec}`;
}

export default function Overview() {
  const { state } = useSim();
  const navigate = useNavigate();
  const pending = state?.pending_decision;
  const task = state?.current_task;

  return (
    <div className="space-y-6 ngx-fade-in">
      <div className="flex items-end justify-between">
        <SectionTitle sub="Vendor-agnostic Human-in-the-Loop supervision layer.">Overview</SectionTitle>
        <Button data-testid="goto-live" onClick={() => navigate("/live")}
          className="h-11 bg-blue-600 hover:bg-blue-700 text-white">
          Open Live Control <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      {pending && (
        <Card className="p-4 border-amber-700/60 bg-amber-950/20 flex items-center justify-between ngx-fade-in" data-testid="overview-pending-alert">
          <div className="flex items-center gap-3">
            <span className="h-2.5 w-2.5 rounded-full bg-amber-500 ngx-pulse" />
            <span className="text-sm text-amber-200 font-medium">
              Human decision required — {pending.event_no} · {pending.ai_recommendation} @ {pending.confidence}%
            </span>
          </div>
          <Button data-testid="overview-review" onClick={() => navigate("/live")}
            className="h-9 bg-amber-600 hover:bg-amber-500 text-white text-sm">Review</Button>
        </Card>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* System status */}
        <Card className="col-span-12 lg:col-span-5 p-5">
          <div className="flex items-center gap-2 mb-3 text-slate-300">
            <Activity className="h-4 w-4 text-blue-400" />
            <h2 className="font-display text-base font-semibold">System Status</h2>
          </div>
          <StatusRow testid="status-core" label="NGX Core" value={state?.core_status || "—"} tone="success" />
          <StatusRow testid="status-simulator" label="Simulator" value={state?.connector_status || "—"} tone="success" />
          <StatusRow testid="status-human" label="Human Control" value={state?.human_control || "—"} tone="primary" />
          <StatusRow testid="status-safety" label="Safety State" value={state?.safety_state || "—"}
            tone={state?.safety_paused ? "danger" : "success"} />
          <StatusRow testid="status-mode" label="Current Mode" value={state?.mode || "—"} tone="primary" />
        </Card>

        {/* Metrics */}
        <div className="col-span-12 lg:col-span-7 grid grid-cols-2 gap-6">
          <Metric icon={Zap} label="Connection Latency" value={`${state?.latency_ms ?? "--"} ms`} tone="text-emerald-400" />
          <Metric icon={Clock} label="Session Duration" value={fmtDuration(state?.session_duration)} tone="text-slate-100" />
          <Metric icon={Gauge} label="AI Confidence Threshold" value={`${state?.confidence_threshold ?? "--"}%`} tone="text-blue-400" />
          <Metric icon={ShieldCheck} label="Active Task" value={task ? `#${String(task.task_id).padStart(4, "0")}` : "—"} tone="text-slate-100" />
        </div>
      </div>

      {/* Concept banner */}
      <Card className="p-6 relative overflow-hidden">
        <div className="absolute inset-0 ngx-grid-overlay opacity-20" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 text-blue-400 mb-3">
            <Cpu className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-widest">NGX Human-in-the-Loop</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 font-display text-sm md:text-base font-medium text-slate-300">
            {["Machine Proposal", "Human Decision", "Action", "Result", "Knowledge"].map((step, i, arr) => (
              <span key={step} className="flex items-center gap-3">
                <span className="px-3 py-1.5 rounded-sm bg-[#050B14] border border-slate-700 text-slate-100">{step}</span>
                {i < arr.length - 1 && <ArrowRight className="h-4 w-4 text-slate-600" />}
              </span>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-4 max-w-2xl">
            Human feedback data is captured for future model improvement and learning workflows. The AI does not retrain itself in this MVP.
          </p>
        </div>
      </Card>
    </div>
  );
}

function Metric({ icon: Icon, label, value, tone }) {
  return (
    <Card className="p-5 flex flex-col justify-between">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        <Icon className="h-4 w-4 text-slate-500" />
      </div>
      <div className={`font-mono text-3xl font-medium mt-4 ${tone}`}>{value}</div>
    </Card>
  );
}
